// ─────────────────────────────────────────────────────────────────────────────
// Active-comp reconciliation — the SINGLE authority on whether the workspace's
// active competition should change. Pure: no React, no storage, no I/O.
//
// WHY THIS EXISTS (2026-07-22)
// The Workspace switcher flickered between "WIRING TEST CUP" and "Pick a comp"
// several times a second. Two writers owned one piece of state and neither
// deferred:
//
//   1. `MyCompDetail.tsx` — "the comp page you're looking at is the active one".
//   2. `workspace.ts` — "if the active comp isn't in visibleComps, reset it".
//
// Opening DIVISIONS_TEST_CUP_9_TEAM_ (status PENDING, therefore absent from the
// visible slice) put those two in an unwinnable argument: the page set it, the
// reconciler yanked it back, forever. What detonated it was that
// `setActiveCompId` was re-created on every render, so MyCompDetail's effect —
// which lists it as a dependency — re-fired on every render.
//
// THE RULE: reconciliation corrects only a GENUINELY UNKNOWN comp — one that
// appears in no list at all (deleted, or stale localStorage). A comp that
// EXISTS but is merely filtered out of the visible slice is left alone, because
// the user navigated there deliberately. Absence from a filtered view is not
// evidence that something is gone.
//
// Corollary, and the property the tests pin: this function must reach a fixed
// point. A reconciler whose output feeds back into its own input and never
// settles IS a render loop.
// ─────────────────────────────────────────────────────────────────────────────

export interface ReconcilableComp {
  id: string;
  status?: string;
}

/**
 * @returns the comp id that should become active, or `null` to change nothing.
 */
export function reconcileActiveComp(args: {
  activeCompId: string | null;
  /** The FULL list — what "does this comp exist at all?" is judged against. */
  comps: ReconcilableComp[];
  /** The filtered slice a picker would show — what we seed a fresh choice from. */
  visibleComps: ReconcilableComp[];
  isLoading: boolean;
}): string | null {
  const { activeCompId, comps, visibleComps, isLoading } = args;

  // Never act on incomplete data. An empty list mid-flight is not proof that a
  // stored comp is gone, and wiping a good selection on a transient render is
  // exactly the kind of thrash this module exists to stop.
  if (isLoading || comps.length === 0) return null;

  const pick = (): string | null => {
    const pool = visibleComps.length > 0 ? visibleComps : comps;
    if (pool.length === 0) return null;
    return (pool.find(c => c.status === 'LIVE') ?? pool[0]).id;
  };

  // Nothing chosen yet — seed one.
  if (!activeCompId) return pick();

  // The comp exists. Leave it alone, even if it is filtered out of the visible
  // slice (PENDING comps, other-org comps under entry scoping). This is the
  // line that ends the fight with MyCompDetail.
  if (comps.some(c => c.id === activeCompId)) return null;

  // Genuinely unknown — deleted, renamed, or stale localStorage. Correct it.
  return pick();
}
